import React, { useEffect, useRef, useState, DragEvent, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { IActionHandler, MessageDisplayType } from "@preveil-api";
import {
  Account, useAppDispatch, Message, useAppSelector, GlobalErrorMessages, MessageAnchors, Pagination, isNumber, MessageHandlerDisplayType, isRootPath,
  DriveRouteType, DriveErrorMessages, DirectoryEntity, EntryItem, RootInfo, DriveUrlParams, useFetchDirectory, useFetchLink, DefaultRoutes, SORT_DIRECTION,
  sortBy, DriveEntryType, useDriveProcessNotification, DriveManagerEventWatch, AppConfiguration, EntryUpdate, useCheckHandleExePresentMutation, useEditFileMutation, CheckHandleExePresentApiResponse, MessageToastTypes,
  SupportRoutes, useDeleteItems, DriveCloudEditFileSyncMessages, TrashConfirmationMessages, TrashErrorMessages, TrashMessageAnchors, TrashSuccessMessages, DriveTypeMapping, DefaultCollectionName, viewerUnsupported,
  buildNavigationLink, useFetchLinkItem, DriveSuccessMessages, useDuplicateFile, DriveMessages, useCollectionRenameNotification, useFSProcessNotification, AsyncJobTypeMap, AsyncJobStatusMap, RenameValidationErrorMessages,
  Collection, FileSizeLimits, DriveLimits, SearchClient, DriveItemType, useEditFileCloudDriveMutation, convertB64toUUID, SupportedPlatform, useStarNode, DriveStarredErrorMessages,
  DriveStarredMessages, useGetPath, useGetPathList, PathInfo, buildFilesyncPath, DriveConstants, DriveUIActionTypes
} from "src/common";
import { Loading, PageHeader } from "src/components";
import { Toolbar, ListView, Breadcrumbs, FileUpload, EmptyMessage, DriveSearch } from ".";
import { FileDownloadAndView, Move } from "./entries";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  root_info: RootInfo;
  router_params: DriveUrlParams;
  drag_event?: { type: string, e: DragEvent };
  setLegacyFileId: Dispatch<SetStateAction<string | undefined>>;
}

const is_web = AppConfiguration.buildForWeb();

// Description: Maps a filesync edit-file error to the user-facing message. Shared by the standard and Cloud Drive edit sessions so both stay in sync if the message set changes.
function mapEditFileError(error: { data?: unknown }): string {
  const error_data = error?.data;
  return error_data === DriveCloudEditFileSyncMessages.filesync_no_app_error ?
    DriveCloudEditFileSyncMessages.filesync_no_default_app_error :
    typeof error_data === "string" && error_data.includes("unsupported") ?
      DriveCloudEditFileSyncMessages.filesync_unsupported_error : error_data as string;
}

function DriveManagerComponent(props: AllProps) {
  const { current_account, root_info, router_params, drag_event, setLegacyFileId } = props;
  const { collection_id, id, file_id, item_type } = router_params;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const is_loading = useAppSelector((state: RootState) => state.drive.is_loading);
  const selected = useAppSelector((state: RootState) => state.drive.selected);
  const pagination = useAppSelector((state: RootState) => state.drive.pagination);
  const reload_drive_list = useAppSelector((state: RootState) => state.drive.reload_drive_list);
  const pagination_info = useAppSelector((state: RootState) => state.drive.pagination_info);
  const current_directory = useAppSelector((state: RootState) => state.drive.current_directory);
  const collection_info = useAppSelector((state: RootState) => state.drive.current_collection);
  const entry_update = useAppSelector((state: RootState) => state.drive.entry_update);
  const upload_request = useAppSelector((state: RootState) => state.drive.upload_request);
  const rename_job = useAppSelector((state: RootState) => state.ui.rename_job);
  const global_local_sync = useAppSelector((state: RootState) => state.account.global_local_sync);
  const new_global_sync_change = useAppSelector((state: RootState) => state.app.new_global_sync_change);
  const platform = useAppSelector((state: RootState) => state.app.os_info?.user_platform);
  const [page, setPage] = useState<string>(DriveLimits.DRIVE_PAGE_DEFAULT);
  const [path_info, setPathInfo] = useState<PathInfo[] | undefined>();
  const [show_move_modal, setShowMoveModal] = useState<boolean>(false);
  const [file_entry, setFileEntry] = useState<EntryItem>();
  const [creating_new_folder, setCreatingNewFolder] = useState<boolean>(false);
  const [search_client, setSearchClient] = useState<SearchClient | undefined>();
  const [media_entries, setMediaEntries] = useState<EntryItem[]>([]);
  const file_upload_ref = useRef<{ click: () => void }>();
  // HOOKS
  const [checkHandleExe] = useCheckHandleExePresentMutation();
  const [editFile] = useEditFileMutation();
  const [editFileCloudDrive] = useEditFileCloudDriveMutation();
  const { fetchDirectory, resetDirectory, error: dir_error } = useFetchDirectory(current_account, true, !is_web);
  const { fetchLink, resetLink, error: link_error } = useFetchLink(current_account, true, !is_web);
  const { fetchLinkEntity, resetLinkEntity } = useFetchLinkItem(current_account, true);
  const { duplicated_file, error: duplicate_error, duplicateFile, resetDuplicate } = useDuplicateFile(current_account);
  const { deleteItems, delete_complete, errors, successes, resetDeleteItems } = useDeleteItems(current_account);
  const { new_notification } = useDriveProcessNotification(current_directory?.collection_id, DriveManagerEventWatch);
  const { full_path_list, getPath } = useGetPath(current_account);
  const { breadcrumbs_paths, buildBreadcrumbs } = useGetPathList(current_account);
  // Handles notifications for FS collection rename jobs
  const { new_notification: rename_job_notification, resetNotifications } = useFSProcessNotification(AsyncJobTypeMap.COLLECTION_RENAME, rename_job?.id);
  // Handles collection rename events from CS
  const { setNodeStarredState, setBulkNodeStarredState, reset, success: star_success, error: star_error, bulk_success, star, warning } = useStarNode(current_account);
  useCollectionRenameNotification(current_account, is_web);

  useEffect(() => {
    // NOTE: Initialize the search client only once at the top level
    !search_client && setSearchClient(new SearchClient(current_account.user_id));
    return () => {
      ResetPagination();
      dispatch(driveActions.resetSort());
      dispatch(uiActions.handleMessageDismiss());
      dispatch(driveActions.setSelected([]));
      dispatch(driveActions.resetCurrentDirectory());
      dispatch(driveActions.resetCurrentPathInfo());
    };
  }, []);

  // Description: Handles normal navigation to other directories
  useEffect(() => {
    bulk_success && handleSuccessSetStarred(DriveStarredMessages.success_bulk_star);
    star_success && !!star && handleSuccessSetStarred(star.is_starred ? DriveStarredMessages.success_star_state.replace(MessageAnchors.file_name, star.name) : DriveStarredMessages.success_unstar_state.replace(MessageAnchors.file_name, star.name));
    star_error && dispatch(uiActions.handleRequestErrors(new Message(DriveStarredErrorMessages.error_star_bulk_anchor))) && reset();
    warning && dispatch(uiActions.handleSetMessage(new Message(DriveStarredMessages.already_starred, MessageHandlerDisplayType.toastr, MessageToastTypes.warning))) && reset();
  }, [star_success, star_error, bulk_success, star, warning]);

  // Description: Handles normal navigation to other directories
  useEffect(() => {
    handleFetchItem(false);
    ((!isRootPath(collection_id)) && !!id) && getPath(collection_id, id);
    // NOTE: Add collection_id to detect direct links failures (Id will be undefined in both instances)
  }, [id, collection_id]);

  // Description: Trigger reload of the same current directory
  useEffect(() => {
    if (reload_drive_list) {
      handleFetchItem();
      dispatch(driveActions.setReloadDriveList(false));
    }
  }, [reload_drive_list]);

  // Description: Handle file duplication
  useEffect(() => {
    if (!!duplicated_file || !!duplicate_error) {
      !!duplicated_file && dispatch(uiActions.handleSetMessage(new Message(DriveSuccessMessages.success_duplicating_file.replace(MessageAnchors.new_name, duplicated_file))));
      !!duplicate_error && dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_duplicating_file)));
      resetDuplicate();
    }
  }, [duplicated_file, duplicate_error]);

  // Description: On change of the  Global Cloud Sync Lock by Admin settings, adjust current NodePermissions
  useEffect(() => {
    // NOTE: Only trigger refresh when current_directory and collection are loaded
    (global_local_sync !== undefined && !!collection_info) && handleFetchItem();
  }, [global_local_sync]);

  useEffect(() => {
    // NOTE: display toaster only when a notification to turn on global sync is received
    if (!!new_global_sync_change && !!global_local_sync && current_account?.org_info) {
      dispatch(
        uiActions.handleSetMessage(new Message(DriveMessages.cloud_lock_on, MessageHandlerDisplayType.toaster, MessageToastTypes.warning))
      );
    }
  }, [new_global_sync_change]);

  // Description: Handle Errors in fetching Link and directory
  useEffect(() => {
    if (link_error || dir_error) {
      link_error && resetLink();
      dir_error && resetDirectory();
      // NOTE: If this is a root path and dir_error then trigger Loading clean up
      !isRootPath(collection_id) && navigate(DefaultRoutes.drive_root);
      is_loading && dispatch(driveActions.setLoading(false));
    }
  }, [link_error, dir_error]);

  // Description: after setting threads clear setLoading
  useEffect(() => {
    if (!!current_directory) {
      DriveManagerRequests.handleCurrentDirectorySuccess(current_directory);
      !!file_id && handleNavigatingIntoFileUrl(current_directory, file_id);

      // NOTE: disable is_loading if item_type is not a file
      (is_loading && item_type !== DriveRouteType.FILE) && dispatch(driveActions.setLoading(false));
    }
  }, [current_directory]);

  useEffect(() => {
    handleDeleteResponse();
  }, [delete_complete]);

  // Description: Update UI when Notifications come in
  useEffect(() => {
    (!!new_notification && !is_loading && !upload_request) && handleFetchItem();
  }, [new_notification]);

  useEffect(() => {
    !!entry_update && updateEntries(entry_update);
  }, [entry_update]);

  // Description: check to see if this is supposed to have a link_entity in current_directory
  //              Get link when the url type is directory but this is a maintainer node and requires link information
  useEffect(() => {
    (!!current_directory?.id && !!collection_info && Collection.isRootChildNode(collection_info, root_info.id, current_directory.id))
      && fetchLinkEntity(collection_info.collection_id);
  }, [collection_info]);

  useEffect(() => {
    if (!!rename_job_notification) {
      if (rename_job_notification.status === AsyncJobStatusMap.COMPLETED) {
        dispatch(uiActions.handleSetMessage(
          new Message(
            DriveSuccessMessages.success_rename.replace(MessageAnchors.prev_name, rename_job?.old_name || "").replace(MessageAnchors.new_name, rename_job?.new_name || ""),
            MessageHandlerDisplayType.toastr,
            MessageToastTypes.success
          )
        ));
      } else if (rename_job_notification.status === AsyncJobStatusMap.FAILED || rename_job_notification.status === AsyncJobStatusMap.HARD_FAILED) {
        dispatch(uiActions.handleSetMessage(
          new Message(
            RenameValidationErrorMessages.error_renaming.replace(MessageAnchors.file_name, rename_job?.old_name ?? ""),
            MessageHandlerDisplayType.toastr,
            MessageToastTypes.error
          )
        ));
      }
      // NOTE: Reset this notification otherwise we will continue to see messages pop up on navigation
      resetNotifications(rename_job_notification.request_id);
    }
  }, [rename_job_notification]);

  // Description: Build Breadcrumbs separately from FetchDirectory
  useEffect(() => {
    !!full_path_list &&
      buildBreadcrumbs(full_path_list, undefined, !!item_type && item_type === DriveRouteType.LINK);
  }, [full_path_list]);

  // Description: Upon fully decrypted, update the main Entry in ListView
  useEffect(() => {
    if (!!breadcrumbs_paths) {
      setPathInfo(breadcrumbs_paths);
      const last_path = breadcrumbs_paths.length > 0 ? breadcrumbs_paths[breadcrumbs_paths.length - 1].path : undefined;
      const directory_name = last_path === DefaultCollectionName ? DriveConstants.ROOT_DIRECTORY : (last_path || DriveConstants.ROOT_DIRECTORY);
      dispatch(driveActions.setCurrentPathInfo({ current_directory_path: buildFilesyncPath(breadcrumbs_paths), directory_name }));
    }
  }, [breadcrumbs_paths]);

  // -------------------------------------------------------------------------------
  // Description: Handle all children component actions and store it
  // -------------------------------------------------------------------------------
  const DriveManagerRequests = {
    // Description: Refresh the data in the current mailbox
    handleRefresh: handleFetchItem,
    handleSetStarState: (params?: { entry: EntryItem, star: boolean }) => {
      if (!!params) {
        const { entry, star } = params;
        !!entry && !!entry.collection_id && !!entry.id && setNodeStarredState(entry, !star);
      } else {
        const items = !!current_directory ? current_directory.entries.filter(entry => selected.includes(entry.id)) : undefined;
        !!items && setBulkNodeStarredState(items);
      }
    },
    // Description: Handles select all from List View header
    handleSelect: (params: { entry?: EntryItem, selected: boolean }) => {
      let new_selected = selected.slice();
      const _id = !!params.entry ? params.entry.id : null;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        // NOTE: Filter out locked files as those entries should not be selected, deleted, etc when they are locked.
        new_selected = [];
        (!!current_directory && params.selected) &&
          _.forEach(current_directory.entries, (entry: EntryItem) => {
            if (!entry.is_locked) { new_selected.push(entry.id); }
          });
      }
      dispatch(driveActions.setSelected(new_selected));
    },

    handleSelectFiles: () => {
      !!file_upload_ref.current && file_upload_ref.current.click();
    },

    // Description: Upload files to a row or a directory
    handleUpload: (params: { destination?: EntryItem, destination_id?: string, files: FileList }) => {
      const { destination, files } = params;
      // Limit the number of files uploaded at a time to DRIVE_MAX_FILE_UPLOAD_LIMIT
      if (files.length > FileSizeLimits.DRIVE_MAX_FILE_UPLOAD_LIMIT) {
        dispatch(uiActions.handleSetMessage(new Message(DriveMessages.file_upload_limit.replace(MessageAnchors.item_count, FileSizeLimits.DRIVE_MAX_FILE_UPLOAD_LIMIT.toString()), MessageHandlerDisplayType.toastr,
          MessageToastTypes.warning)));
        return;
      }
      const linked_collection_id = destination?.type === DriveEntryType.LINK ? destination?.linked_collection_id : undefined;
      const link_id = linked_collection_id ||
        (!!collection_info && collection_info.collection_name !== DefaultCollectionName ? collection_info.collection_id : undefined);
      const destination_id = destination?.id || params.destination_id;
      // NOTE: using link_collection_id to identify LINK rows and node_id for DIR rows. 
      const tracker_id = !!destination ? (destination?.type !== DriveEntryType.LINK ? destination_id : link_id) :
        (!!collection_info && (collection_info.maintainer_id === collection_info.id && !!collection_info.is_root)) ? link_id : destination_id;
      !!destination && dispatch(driveActions.setSelected(selected.filter(id => id !== destination.id)));
      if (!destination_id) return;
      const _collection_id = destination?.collection_id || current_directory?.collection_id;
      const node_id = destination?.id || current_directory?.id;
      if (!_collection_id || !node_id) return;
      const mapped_files: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        !!file && mapped_files.push(file);
      }
      const upload = {
        collection_id: _collection_id,
        node_id,
        files: mapped_files,
        linked_collection_id,
        destination: {
          directory: !destination ? current_directory : undefined,
          id: destination_id,
          tracker_id
        }
      };
      dispatch(driveActions.setFileUploadRequest(upload));
    },

    // Description: Delete single or bulk items with a confirmation warning
    handleDelete: (entry?: EntryItem) => {
      if (!!entry || !!current_directory) {
        const items = !!entry ? [entry] : !!current_directory ? current_directory.entries.filter(entry => selected.includes(entry.id)) : [];
        const delete_items = items.map(entry => ({ id: entry.id, collection_id: entry.collection_id, type: entry.type }));
        let message: string = "";
        let title: string = "";
        if (items.length === 1 && items[0].type === DriveEntryType.LINK) {
          // Deleting one shared folder
          title = TrashConfirmationMessages.delete_link_title.replace(TrashMessageAnchors.name, items[0].name);
          message = TrashConfirmationMessages.delete_link;
        } else if (items.length > 1 && getSelectedItemTypes(items).length === 1 && getSelectedItemTypes(items)[0] === DriveEntryType.LINK) {
          // Deleting several shared folders
          title = TrashConfirmationMessages.delete_links_title;
          message = TrashConfirmationMessages.delete_links.replace(TrashMessageAnchors.item_count, `${selected.length}`);
        } else if (items.length > 1 && getSelectedItemTypes(items).length > 1) {
          // Deleting a mix of items
          title = TrashConfirmationMessages.delete_mixed_items_title.replace(TrashMessageAnchors.item_count, `${selected.length}`);
          message = TrashConfirmationMessages.delete_mixed_items.replace(TrashMessageAnchors.item_count, `${selected.length}`);
        } else {
          // Default message
          const delete_display = items.length === 1 ? `"${items[0].name}"` : `${selected.length} items`;
          message = TrashConfirmationMessages.confirm_delete_message.replace(TrashMessageAnchors.name, delete_display);
          title = `Delete ${items.length === 1 ? `${DriveTypeMapping[items[0].type]}: "${items[0].name}"` : `${selected.length} items`}?`;
        }
        const confirmation_dialog = new Message(message, MessageHandlerDisplayType.confirm, MessageToastTypes.primary, title,
          {
            label: "Yes",
            data: true,
            action: () => { dispatch(driveActions.setLoading(true)); deleteItems(delete_items); },
          },
          { label: "No" },
        );
        dispatch(uiActions.handleSetMessage(confirmation_dialog));
      }
    },

    // Description: View File
    handleDownloadAndView: (entry: EntryItem) => {
      // NOTE: Dismiss any active toastr (e.g., "file not supported") before opening the viewer.
      dispatch(uiActions.handleMessageDismiss());
      !is_loading && dispatch(driveActions.setLoading(true));
      setFileEntry(entry);
    },

    // Description: Download File
    handleDownload: (entry?: EntryItem) => {
      const _entries = !!entry ? [entry] : !!current_directory ? current_directory.entries.filter(entry => selected.includes(entry.id)) : [];
      const file_entries = _entries.filter(entry => entry.type === DriveEntryType.FILE);
      const download_requests = file_entries.map(entry => ({
        collection_id: entry.collection_id,
        file_id: entry.id,
        file_name: entry.name
      }));
      if (file_entries.length !== _entries.length) {
        dispatch(uiActions.handleSetMessage(new Message(DriveMessages.only_files_can_be_downloaded, MessageHandlerDisplayType.toastr,
          MessageToastTypes.warning)));
      }
      download_requests.length > 0 && dispatch(driveActions.setFileDownloadRequest(download_requests));
      dispatch(driveActions.setSelected([]));
    },

    // Description: Create new Folder
    handleCreateFolder: (_entry: EntryItem) => {
      setCreatingNewFolder(true);
    },

    // Description: Send Toolbar Details action to the SidebarPanel in Listview (opens the Details tab)
    handleDetails: (current_directory_id: string) => {
      dispatch(driveActions.setActiveShareId({ id: current_directory_id, action: DriveUIActionTypes.Details }));
    },

    // Description: Edit File
    handleEditFile: (entry: EntryItem) => {
      // For Windows, use Cloud Drive session directly
      if (platform === SupportedPlatform.WIN) {
        startEditFileCloudDriveSession(entry);
      } else {
        checkHandleExePresent(entry);
      }
    },

    // Description: Duplicate File
    handleDuplicateFile: (entry: EntryItem) => {
      const confirmation_dialog = new Message(`Are you sure you want to duplicate ${entry.name}?`, MessageHandlerDisplayType.confirm, MessageToastTypes.primary, `Duplicate File ${entry.name}?`,
        {
          label: "Yes",
          data: true,
          action: () => duplicateFile(entry.collection_id, entry.id, entry.name),
        },
        { label: "No" },
      );
      dispatch(uiActions.handleSetMessage(confirmation_dialog));
    },

    handleMoveModal: (params: { show: boolean, entry?: EntryItem }) => {
      const { show, entry } = params;
      !!entry && dispatch(driveActions.setSelected([entry.id]));
      setShowMoveModal(show);
    },

    //  Description: Navigate to page passed - different behavior for search
    handlePaging: (new_page: string) => {
      (new_page !== page && isNumber(new_page)) ? setPage(new_page) :
        DriveManagerRequests.handlePageErrorMessage({
          message: GlobalErrorMessages.default, stack: {
            error: DriveErrorMessages.error_paginating.replace(MessageAnchors.page_number, new_page)
          }
        });
    },

    // Description: On successful get Directory and set pagination object
    handleCurrentDirectorySuccess: (_current_directory: DirectoryEntity) => {
      // NOTE: Build pagination for current_directory
      const total_page_rows = _current_directory.entries.length;
      const total_rows = pagination_info?.total_count || _current_directory.entries.length;
      if (total_page_rows > 0 && total_rows > 0) {
        const pagination_item = new Pagination(Pagination.getPageIndex(page), total_page_rows, total_rows, pagination.pageSize).pagination_item;
        !!pagination_item && dispatch(driveActions.setPagination(pagination_item));
      } else {
        dispatch(driveActions.resetPagination());
      }
    },

    // Description: Filter media entries from entries
    handleFilterMediaEntries(entries: EntryItem[]) {
      const filtered_media = entries.filter(
        entry => (entry.type_label === DriveItemType.Image || entry.type_label === DriveItemType.Video)) || [];
      setMediaEntries(filtered_media);
    },

    // Description: Set file entry
    handleSetFileEntry(entry: EntryItem) {
      setFileEntry(entry);
    },

    // Description: Reset File Entry on close and on error
    handleResetFileEntry() {
      setFileEntry(undefined);
      setLegacyFileId(undefined);
    },

    // Description: Handle all page / components top level errors
    handlePageErrorMessage: (params: { message: string, stack?: any, display_type?: MessageDisplayType }) => {
      const display_type = !!params.display_type ? params.display_type : MessageHandlerDisplayType.logger;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DriveManagerRequests as any)[callback] instanceof Function) {
      (DriveManagerRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DriveManagerRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // Description: Fetch the correct list view depending on type and collection_id / slug
  function handleFetchItem(reset_pagination: boolean = true) {
    !is_loading && dispatch(driveActions.setLoading(true));
    const is_root_path = isRootPath(collection_id);
    // NOTE: Reset several states when navigating (i.e. not for refresh and others)
    if (!reset_pagination) {
      ResetPagination();
      is_root_path && setPathInfo(undefined);
      dispatch(driveActions.resetCurrentPathInfo());
    }
    resetLinkEntity();
    dispatch(driveActions.setSelected([]));

    // NOTE: Fetch the current item
    if (is_root_path) {
      // NOTE: Fetch root with root_info
      fetchDirectory(root_info.collection_id, root_info.id);
    } else {

      if (!!collection_id && item_type === DriveRouteType.DIRECT_LINK) {
        // NOTE: Handle Direct links from legacy V1
        fetchLink(collection_id, "", true);
      } else if (!!collection_id && !!id && !!item_type) {
        item_type === DriveRouteType.DIR || item_type === DriveRouteType.FILE ? fetchDirectory(collection_id, id) :
          item_type === DriveRouteType.LINK ? fetchLink(collection_id, id) :
            DriveManagerRequests.handlePageErrorMessage({
              message: DriveErrorMessages.default,
              stack: { error_message: DriveErrorMessages.incorrect_entry_type },
              display_type: MessageHandlerDisplayType.toastr
            });
      } else {
        DriveManagerRequests.handlePageErrorMessage({ message: DriveErrorMessages.error_access_drive, stack: { error_message: DriveErrorMessages.incorrect_entry_type }, display_type: MessageHandlerDisplayType.toastr });
        dispatch(driveActions.setLoading(false));
      }

    }
  }

  // Description: Handle the complete response after delete
  function handleDeleteResponse() {
    if (delete_complete && !!current_directory) {
      if (errors.length === 0) {
        if (successes.length === 1) {
          const item = current_directory.entries.find(i => i.id === successes[0]);
          if (!!item) {
            const message = item.type === DriveEntryType.LINK ? TrashSuccessMessages.success_hiding_one : TrashSuccessMessages.success_deleting_one;
            dispatch(uiActions.handleSetMessage(new Message(message.replace(TrashMessageAnchors.name, item.name))));
          }
        } else {
          dispatch(uiActions.handleSetMessage(new Message(TrashSuccessMessages.success_deleting_many.replace(MessageAnchors.item_count, successes.length.toString()))));
        }
      } else if (successes.length === 0) {
        if (errors.length === 1) {
          const item = current_directory.entries.find(i => i.id === errors[0]);
          !!item && dispatch(uiActions.handleRequestErrors(new Message(TrashErrorMessages.error_deleting_one_item.replace(TrashMessageAnchors.name, item.name))));
        } else {
          dispatch(uiActions.handleRequestErrors(new Message(TrashErrorMessages.error_deleting_all_items.replace(TrashMessageAnchors.item_count, errors.length.toString()))));
        }
      } else {
        dispatch(
          uiActions.handleRequestErrors(
            new Message(
              TrashErrorMessages.error_deleting_some_items
                .replace(TrashMessageAnchors.item_count, errors.length.toString())
                .replace(TrashMessageAnchors.total_count, selected.length.toString()),
            ),
          ),
        );
      }
      handleFetchItem();
      dispatch(driveActions.setSelected([]));
      resetDeleteItems();
    }
  }

  // Description: Handle Success after toggle - If bulk action and there are failures then pass a warning
  function handleSuccessSetStarred(message: string) {
    dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.success)));
    handleFetchItem();
    reset();
  }

  // Description: Checks to see if the user has the handle.exe utility downloaded on their computer (this file is needed to edit on my preveil)
  // if it is not present, a modal pops up instructing the user to install handle.exe. If it is present, we start the edit file session
  async function checkHandleExePresent(entry: EntryItem) {
    await checkHandleExe({ user_id: current_account.user_id })
      .unwrap()
      .then((response: CheckHandleExePresentApiResponse) => {
        response === "OK" ? startEditFileSession(entry) : InstallMissingSoftwareModal();
      })
      .catch(() => {
        // NOTE: Dialog to alert the user instructions on how to install the Cloud editor (no error logging needed)
        InstallMissingSoftwareModal();
      });
  }

  // Description: Given a list of EntryItem, Get a set of selected item types (specified by DriveEntryType)
  function getSelectedItemTypes(entries: EntryItem[]) {
    const types = _.map(entries, (entry: EntryItem) => entry.type);
    return _.uniq(types);
  }

  // Description: This is the modal instructing the user to install the handle.exe utility. It will take them to a support page.
  function InstallMissingSoftwareModal() {
    const confirmation_dialog = new Message(DriveCloudEditFileSyncMessages.install_handle_exe, MessageHandlerDisplayType.confirm, MessageToastTypes.primary, "Install Missing Software",
      {
        label: "See Instructions",
        data: true,
        action: () => { window.open(SupportRoutes.handle_exe, "_blank"); },
      },
      { label: "Cancel" },
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: this starts the edit file session and catches any errors. some errors are: "no available apps to open the file", "received unsupported extension"
  async function startEditFileSession(entry: EntryItem) {
    // NOTE MAYBE TO DO: Implement useFSProcessNotification for on closing the app. Would need to pass a request_id in this call.
    await editFile({
      user_id: current_account.user_id,
      collection_id: entry.collection_id,
      file_id: entry.id,
      file_name: entry.name
    }).unwrap()
      .then(() => {
        // Add message for waiting to open folder
        dispatch(uiActions.handleSetMessage(new Message(DriveCloudEditFileSyncMessages.opening_app_message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
      })
      .catch((error) => {
        dispatch(uiActions.handleRequestErrors(new Message(mapEditFileError(error)), error));
      });
  }

  // Description: this starts the edit file session using the Cloud Drive API for Windows platforms
  async function startEditFileCloudDriveSession(entry: EntryItem) {
    const dir_id = entry.parent_id || current_directory?.id;
    if (!dir_id) {
      dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_fetching_file)));
      return;
    }
    await editFileCloudDrive({
      user_id: current_account.user_id,
      collection_id: convertB64toUUID(entry.collection_id),
      directory_id: convertB64toUUID(dir_id),
      file_id: convertB64toUUID(entry.id),
      file_name: entry.name,
    }).unwrap()
      .then(() => {
        // Add message for waiting to open folder
        dispatch(uiActions.handleSetMessage(new Message(DriveCloudEditFileSyncMessages.opening_app_message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
      })
      .catch((error) => {
        dispatch(uiActions.handleRequestErrors(new Message(mapEditFileError(error)), error));
      });
  }

  // Description: Reset state supporting client side pagination
  function ResetPagination() {
    setPage(DriveLimits.DRIVE_PAGE_DEFAULT);
    dispatch(driveActions.resetPagination());
  }

  // Description: Keep track of new files uploaded to current directory
  function updateEntries(entry_update: EntryUpdate) {
    if (entry_update.new_entries > 0 && !!current_directory && entry_update.destination_id === current_directory.id) {
      const entries = sortBy(current_directory.entries.concat(entry_update.latest), { field: "name", direction: SORT_DIRECTION.ascending });
      dispatch(driveActions.setCurrentDirectorySuccess(Object.assign({}, current_directory, { entries })));
    }
  }

  // Description: Handle navigation into a file url
  function handleNavigatingIntoFileUrl(_current_directory: DirectoryEntity, _file_id: string) {
    const entry = _current_directory.entries.find((entry: EntryItem) => entry.id === _file_id);
    if (!!entry && entry.type === DriveEntryType.FILE && !viewerUnsupported(entry)) {
      setFileEntry(entry);
    } else {
      if (!!entry) {
        // NOTE: Unsupported file id
        dispatch(uiActions.handleSetMessage(new Message(DriveErrorMessages.file_not_supported, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
        setLegacyFileId(undefined);
      } else {
        dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.file_not_found, MessageHandlerDisplayType.toastr)));
      }
      // NOTE: Navigate to parent directory and stop loading
      (isRootPath(collection_id) || (root_info.collection_id === collection_id && root_info.id === id)) ?
        navigate(DefaultRoutes.drive_root) :
        // TODO: If link needs to navigate to link specific url (only an issue for legacy, V1 Direct Links)
        navigate(buildNavigationLink(collection_id, id || _current_directory.id, DriveEntryType.DIR));
      dispatch(driveActions.setLoading(false));
    }

  }

  // Description: Render the correct view
  function RenderView() {
    const props = { current_account, selected, is_loading, page, pagination };
    return !!current_directory ?
      <ListView {...props}
        current_directory={current_directory}
        creating_new_folder={creating_new_folder}
        setCreatingNewFolder={setCreatingNewFolder}
        drag_event={drag_event}
        handleAction={handlePageActions} /> : is_loading ?
        <Loading className="in-place mt-5" /> :
        <EmptyMessage />;
  }

  return <>
    <PageHeader>
      <div className="header-flex">
        {
          (!!collection_info && !!path_info) ?
            <Breadcrumbs
              current_account={current_account}
              id={id}
              path_info={path_info}
              collection_info={collection_info}
              handleAction={handlePageActions} /> : <h1>My PreVeil</h1>
        }{
          (!!collection_info && !!current_directory && !!search_client) &&
          <DriveSearch
            search_client={search_client}
            current_account={current_account}
            collection_info={collection_info}
            current_directory={current_directory}
            pagination_info={pagination_info}
            selected={selected} />
        }
      </div>
      <Toolbar
        current_account={current_account}
        collection_info={collection_info}
        is_loading={is_loading}
        is_root={isRootPath(collection_id)}
        selected={selected}
        pagination={pagination}
        current_directory={current_directory}
        handleAction={handlePageActions} />
    </PageHeader>
    <FileUpload ref={file_upload_ref} current_directory={current_directory} handleAction={handlePageActions} />
    <div id="outer-scope" className="cover-content">
      {RenderView()}
    </div>
    {
      (!!show_move_modal && !!current_directory && !!collection_info) &&
      <Move
        current_account={current_account}
        current_directory={current_directory}
        collection_info={collection_info}
        count={selected.length}
        handleAction={handlePageActions}
      />
    }

    {(!!file_entry && !!collection_info && !!current_directory) && (
      <FileDownloadAndView
        file_entry={file_entry}
        current_account={current_account}
        current_directory={current_directory}
        root_info={root_info}
        collection_info={collection_info}
        media_entries={media_entries}
        handleAction={handlePageActions}
      />
    )}
  </>;
}

export default React.memo(DriveManagerComponent);
